import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, Wrench, Database, Zap, ChevronDown, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

import { systemApi } from '../../../api/system.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { Card } from '../../../components/ui/Card.jsx';
import { KpiCard } from '../../../components/ui/KpiCard.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.jsx';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { fmt } from '../../../lib/formatters.js';
import { t } from '../../../lib/i18n.js';

function formatUptime(sec) {
  if (sec == null) return '-';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d} g ${h} h`;
  if (h > 0) return `${h} h ${m} min`;
  if (m > 0) return `${m} min`;
  return `${sec} s`;
}

function mb(bytes) {
  return bytes ? `${Math.round(bytes / 1048576)} MB` : '-';
}

// Friendly, clickable cache sections mapped to the cache tags the backend uses.
// A non technical user picks the area to refresh instead of typing tag names.
const CACHE_SECTIONS = [
  { tag: 'products', label: 'Prodotti', desc: 'Catalogo e disponibilità' },
  { tag: 'brands', label: 'Brand', desc: 'Marche del catalogo' },
  { tag: 'categories', label: 'Categorie', desc: 'Categorie del catalogo' },
  { tag: 'stores', label: 'Negozi', desc: 'Punti vendita' },
  { tag: 'roles', label: 'Ruoli', desc: 'Ruoli e accessi' },
  { tag: 'permissions', label: 'Permessi', desc: 'Catalogo permessi' },
  { tag: 'feedback', label: 'Feedback AI', desc: 'Riscontri sulle risposte AI' },
  { tag: 'analytics', label: 'Statistiche', desc: 'Dashboard e report' }
];

function DependencyCard({ icon: Icon, name, description, up, loading }) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-subtle text-ink-muted">
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-ink">{name}</p>
            <p className="text-xs text-ink-muted">{description}</p>
          </div>
        </div>
        {loading
          ? <Skeleton className="h-6 w-20" />
          : <Badge tone={up ? 'success' : 'danger'}>{up ? 'Online' : 'Offline'}</Badge>}
      </div>
    </Card>
  );
}

export function SystemPage() {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const { run, mfaProps } = useMfaStepUp();

  const canMaintenance = hasPermission('system:maintenance');
  const canPurgeCache = hasPermission('settings:update');

  const healthQuery = useQuery({ queryKey: ['system', 'health'], queryFn: systemApi.health, refetchInterval: 15_000 });
  const metricsQuery = useQuery({ queryKey: ['system', 'metrics'], queryFn: systemApi.metrics, refetchInterval: 15_000 });
  const maintenanceQuery = useQuery({ queryKey: ['system', 'maintenance'], queryFn: systemApi.maintenance, refetchInterval: 30_000 });

  const [busyTag, setBusyTag] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [enableMaintOpen, setEnableMaintOpen] = useState(false);
  const [maintMessage, setMaintMessage] = useState('');
  const [maintReason, setMaintReason] = useState('');
  const [techOpen, setTechOpen] = useState(false);

  const invalidate = useMutation({
    mutationFn: (section) => systemApi.invalidateCache([section.tag]),
    onSuccess: (_d, section) => toast.success(`"${section.label}" aggiornata.`),
    onError: (err) => toast.error(err.message || t('common.error')),
    onSettled: () => setBusyTag(null)
  });

  const purgeAll = useMutation({
    mutationFn: () => systemApi.purgeAllCache(),
    onSuccess: () => { toast.success('Cache svuotata.'); setPurgeOpen(false); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const enableMaint = useMutation({
    mutationFn: () => run(() => systemApi.enableMaintenance({ message: maintMessage || undefined, reason: maintReason || undefined })),
    onSuccess: () => {
      toast.success('Manutenzione attivata.');
      queryClient.invalidateQueries({ queryKey: ['system', 'maintenance'] });
      setEnableMaintOpen(false); setMaintMessage(''); setMaintReason('');
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const disableMaint = useMutation({
    mutationFn: () => systemApi.disableMaintenance({}),
    onSuccess: () => { toast.success('Manutenzione disattivata.'); queryClient.invalidateQueries({ queryKey: ['system', 'maintenance'] }); },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const health = healthQuery.data;
  const metrics = metricsQuery.data;
  const maint = maintenanceQuery.data;

  const dbUp = health?.dependencies?.mongo?.up;
  const cacheUp = health?.dependencies?.redis?.up;
  const allUp = Boolean(dbUp && cacheUp);
  const proc = metrics?.process;
  const req = metrics?.requests;
  const total = req?.total || 0;
  const ok2xx = req?.byStatusClass?.['2xx'] || 0;
  const successRate = total ? ok2xx / total : null;

  const loadingTop = healthQuery.isLoading || maintenanceQuery.isLoading;

  let hero;
  if (loadingTop) {
    hero = { tone: 'neutral', icon: CheckCircle2, title: 'Controllo in corso...', text: '' };
  } else if (maint?.enabled) {
    hero = { tone: 'warning', icon: Wrench, title: 'Manutenzione attiva', text: 'Il sistema è in pausa per gli utenti. Tu e gli IP autorizzati potete continuare.' };
  } else if (allUp) {
    hero = { tone: 'success', icon: CheckCircle2, title: 'Tutto operativo', text: 'Il sistema e i suoi servizi funzionano correttamente.' };
  } else {
    hero = { tone: 'danger', icon: AlertTriangle, title: 'Problema rilevato', text: 'Uno dei servizi essenziali non risponde. Controlla i dettagli qui sotto.' };
  }

  const HERO_STYLES = {
    success: 'border-success/30 bg-success/5',
    danger: 'border-danger/30 bg-danger/5',
    warning: 'border-warning/30 bg-warning/5',
    neutral: 'border-border bg-surface'
  };
  const HERO_ICON_TONE = {
    success: 'text-success',
    danger: 'text-danger',
    warning: 'text-warning',
    neutral: 'text-ink-muted'
  };
  const HeroIcon = hero.icon;

  const refreshSection = (section) => {
    setBusyTag(section.tag);
    invalidate.mutate(section);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t('nav.system')} description="Lo stato del sistema in tempo reale: servizi, prestazioni, manutenzione e cache." />

      {/* Stato generale */}
      <Card className={`flex items-center gap-4 border ${HERO_STYLES[hero.tone]}`}>
        <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-surface-elevated ${HERO_ICON_TONE[hero.tone]}`}>
          <HeroIcon className="h-6 w-6" aria-hidden="true" />
        </span>
        <div>
          <p className="text-lg font-semibold text-ink">{hero.title}</p>
          {hero.text && <p className="text-sm text-ink-muted">{hero.text}</p>}
        </div>
      </Card>

      {/* KPI rapidi */}
      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard label="Attivo da" loading={metricsQuery.isLoading} value={proc ? formatUptime(proc.uptimeSec) : '-'} sub="Dall'ultimo riavvio" />
        <KpiCard label="Richieste servite" loading={metricsQuery.isLoading} value={fmt.int(total)} sub="Dall'ultimo riavvio" />
        <KpiCard label="Tasso di successo" loading={metricsQuery.isLoading} value={successRate == null ? '-' : fmt.pct(successRate)} sub={req ? `${fmt.int(req.errors5xx || 0)} errori server` : null} />
        <KpiCard label="Memoria usata" loading={metricsQuery.isLoading} value={proc ? mb(proc.memory?.rssBytes) : '-'} sub="Consumo del processo" />
      </section>

      {/* Servizi essenziali */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <DependencyCard icon={Database} name="Database" description="Archivia utenti, prodotti, ordini e impostazioni." up={dbUp} loading={healthQuery.isLoading} />
        <DependencyCard icon={Zap} name="Cache" description="Velocizza le pagine e gestisce le sessioni." up={cacheUp} loading={healthQuery.isLoading} />
      </section>

      {/* Prestazioni + Manutenzione */}
      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <header className="mb-4">
            <h2 className="text-base font-semibold text-ink">Prestazioni</h2>
            <p className="text-xs text-ink-muted">Riepilogo delle richieste dall'ultimo riavvio.</p>
          </header>
          {metricsQuery.isLoading ? <Skeleton className="h-28 w-full" /> : (
            <dl className="flex flex-col divide-y divide-border text-sm">
              {[
                ['Richieste totali', fmt.int(total), 'text-ink'],
                ['Completate con successo', fmt.int(ok2xx), 'text-success'],
                ['Richieste rifiutate', fmt.int(req?.byStatusClass?.['4xx'] || 0), 'text-warning'],
                ['Errori del server', fmt.int(req?.errors5xx || 0), 'text-danger'],
                ['Tempo medio di risposta', fmt.durationMs(req?.latencyMsAverage || 0), 'text-ink']
              ].map(([label, value, tone]) => (
                <div key={label} className="flex items-center justify-between py-2">
                  <dt className="text-ink-muted">{label}</dt>
                  <dd className={`font-medium tabular-nums ${tone}`}>{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </Card>

        <Card>
          <header className="mb-3">
            <h2 className="text-base font-semibold text-ink">Modalità manutenzione</h2>
            <p className="text-xs text-ink-muted">Mette il sistema in pausa per gli utenti, mostrando un messaggio. Tu e gli IP autorizzati continuate a lavorare.</p>
          </header>
          <div className="mb-4 flex items-center gap-2">
            <span className="text-sm text-ink-muted">Stato attuale:</span>
            <Badge tone={maint?.enabled ? 'danger' : 'success'}>{maint?.enabled ? 'Attiva' : 'Disattivata'}</Badge>
          </div>
          {maint?.enabled && maint?.message && (
            <p className="mb-3 rounded-md border border-border bg-surface px-3 py-2 text-xs text-ink-muted">Messaggio mostrato: {maint.message}</p>
          )}
          {!canMaintenance ? (
            <p className="text-sm text-ink-muted">Non hai il permesso per gestire la manutenzione.</p>
          ) : maint?.enabled ? (
            <Button variant="danger" onClick={() => disableMaint.mutate()} loading={disableMaint.isPending}>Disattiva manutenzione</Button>
          ) : (
            <Button variant="secondary" onClick={() => setEnableMaintOpen(true)}>Attiva manutenzione</Button>
          )}
          {maint?.bypassIps?.length > 0 && (
            <p className="mt-3 text-xs text-ink-faint">Indirizzi sempre autorizzati: {maint.bypassIps.join(', ')}</p>
          )}
        </Card>
      </section>

      {/* Cache */}
      <Card>
        <header className="mb-4">
          <h2 className="text-base font-semibold text-ink">Cache (velocità del sistema)</h2>
          <p className="text-xs text-ink-muted">La cache memorizza i risultati per rendere le pagine più veloci. Se dopo una modifica vedi ancora dati vecchi, aggiorna la sezione interessata.</p>
        </header>
        {canPurgeCache ? (
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-2 text-sm font-medium text-ink">Aggiorna una sezione</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {CACHE_SECTIONS.map((s) => {
                  const busy = busyTag === s.tag && invalidate.isPending;
                  return (
                    <button
                      key={s.tag}
                      type="button"
                      onClick={() => refreshSection(s)}
                      disabled={invalidate.isPending}
                      className="flex items-center justify-between gap-3 rounded-lg border border-border bg-surface-elevated px-3 py-2.5 text-left transition-colors hover:border-border-strong hover:bg-surface-subtle disabled:opacity-50"
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-ink">{s.label}</span>
                        <span className="block truncate text-xs text-ink-muted">{s.desc}</span>
                      </span>
                      <RefreshCw className={`h-4 w-4 shrink-0 text-ink-muted ${busy ? 'animate-spin' : ''}`} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <p className="mb-1 text-sm font-medium text-ink">Oppure svuota tutto</p>
              <p className="mb-3 text-xs text-ink-muted">Azzera l'intera cache. Le prime pagine saranno un po' più lente, poi tutto torna normale. Nessun dato viene perso.</p>
              <Button variant="danger" onClick={() => setPurgeOpen(true)}>Svuota tutta la cache</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink-muted">Non hai il permesso per gestire la cache.</p>
        )}
      </Card>

      {/* Dettagli tecnici */}
      <Card padded={false}>
        <button type="button" onClick={() => setTechOpen((v) => !v)} className="flex w-full items-center justify-between px-6 py-4 text-left">
          <span className="text-sm font-medium text-ink">Dettagli tecnici</span>
          <ChevronDown className={`h-4 w-4 text-ink-muted transition-transform ${techOpen ? 'rotate-180' : ''}`} />
        </button>
        {techOpen && (
          <div className="border-t border-border px-6 py-4">
            {metricsQuery.isLoading || healthQuery.isLoading ? <Skeleton className="h-24 w-full" /> : (
              <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
                {[
                  ['Versione Node.js', proc?.nodeVersion || '-'],
                  ['PID processo', proc?.pid ?? '-'],
                  ['Memoria heap usata', proc ? mb(proc.memory?.heapUsedBytes) : '-'],
                  ['Memoria heap totale', proc ? mb(proc.memory?.heapTotalBytes) : '-'],
                  ['Richieste in corso', fmt.int(req?.inFlight || 0)],
                  ['Database (readyState)', health?.dependencies?.mongo?.readyState ?? '-']
                ].map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between border-b border-border py-1.5 sm:border-0">
                    <dt className="text-ink-muted">{label}</dt>
                    <dd className="font-mono text-xs text-ink">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={purgeOpen}
        title="Svuotare tutta la cache?"
        description="Tutti i dati memorizzati saranno rimossi. Le prime pagine caricate dopo lo svuotamento saranno leggermente più lente, poi tutto torna normale. Nessun dato viene perso."
        destructive
        confirmLabel="Svuota tutto"
        loading={purgeAll.isPending}
        onCancel={() => setPurgeOpen(false)}
        onConfirm={() => purgeAll.mutate()}
      />

      <ConfirmDialog
        open={enableMaintOpen}
        title="Attivare la manutenzione?"
        description="Gli utenti vedranno un messaggio di manutenzione e non potranno usare il sistema. Tu e gli indirizzi IP autorizzati continuerete a lavorare. Sarà richiesta una verifica MFA recente."
        destructive
        confirmLabel="Attiva"
        loading={enableMaint.isPending}
        onCancel={() => setEnableMaintOpen(false)}
        onConfirm={() => enableMaint.mutate()}
      />

      <MfaPrompt {...mfaProps} />
    </div>
  );
}
